import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAdminList } from '../../services/adminService';
import './AdminManagementRedesign.css';

const PAGE_SIZE = 15;
const PAGE_GROUP_SIZE = 5;

function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window.atob(base64).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function AdminManagementRedesign() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const user = token ? parseJwt(token) : null;
  const isSuperAdmin = Number(user?.auth) === 1;

  useEffect(() => {
    let active = true;

    fetchAdminList(token)
      .then(list => {
        if (active) setAdmins(Array.isArray(list) ? list : []);
      })
      .catch(err => {
        console.error('관리자 목록 조회 실패:', err);
        if (active) setError('관리자 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [token]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return admins;

    return admins.filter(admin =>
      [
        admin.name,
        admin.department,
        admin.auth_name,
        admin.username,
        admin.email,
        admin.mobile_phone,
        admin.phone,
        admin.last_login
      ].some(value =>
        String(value ?? '').toLowerCase().includes(search)
      )
    );
  }, [admins, query]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);

  const visible = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const startPage =
    Math.floor((currentPage - 1) / PAGE_GROUP_SIZE) * PAGE_GROUP_SIZE + 1;
  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, pages);

  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );

  const handleSearch = () => {
    setQuery(keyword);
    setPage(1);
  };

  const handleReset = () => {
    setKeyword('');
    setQuery('');
    setPage(1);
  };

  const openDetail = admin => {
    navigate(`/admin/${admin.id}`, {
      state: { admin: { ...admin } }
    });
  };

  return (
    <div className="am-page">
      <div className="am-heading">
        <div>
          <h2>관리자 관리</h2>
          <p>관리자 계정 및 소속·권한 현황</p>
        </div>
        <span className="am-total">전체 {admins.length}명</span>
      </div>

      <div className="am-panel">
        <div className="am-search">
          <input
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="이름, 소속, 권한, 아이디, 이메일, 연락처 검색"
          />
          <button type="button" onClick={handleSearch}>검색</button>
          <button type="button" onClick={handleReset}>초기화</button>
        </div>

        <div className="am-table-top">
          <strong>관리자 목록</strong>
          <span>행을 클릭하면 상세 정보를 확인할 수 있습니다.</span>
        </div>

        <div className="am-table-wrap">
          <table className="am-table">
            <thead>
              <tr>
                <th>이름</th>
                <th>소속</th>
                <th>권한</th>
                <th>아이디</th>
                <th>이메일</th>
                <th>휴대폰 번호</th>
                <th>내선번호</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="am-empty">관리자 목록을 불러오는 중입니다.</td></tr>
              ) : error ? (
                <tr><td colSpan="7" className="am-empty">{error}</td></tr>
              ) : visible.length ? (
                visible.map(admin => (
                  <tr
                    key={admin.id}
                    tabIndex={0}
                    onClick={() => openDetail(admin)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') openDetail(admin);
                    }}
                  >
                    <td className="am-name">{admin.name || '-'}</td>
                    <td>{admin.department || '-'}</td>
                    <td>{admin.auth_name || '-'}</td>
                    <td>{admin.username || '-'}</td>
                    <td>{admin.email?.trim() ? admin.email : '-'}</td>
                    <td>{admin.mobile_phone || '-'}</td>
                    <td>{admin.phone || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="am-empty">조건에 맞는 관리자가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="am-bottom">
          <span>
            {filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
            {' – '}
            {Math.min(currentPage * PAGE_SIZE, filtered.length)}
            {' / '}{filtered.length}명
          </span>

          <div className="am-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
            >이전</button>

            {pageNumbers.map(number => (
              <button
                key={number}
                className={number === currentPage ? 'am-current' : ''}
                onClick={() => setPage(number)}
              >{number}</button>
            ))}

            <button
              disabled={currentPage === pages}
              onClick={() => setPage(currentPage + 1)}
            >다음</button>
          </div>
        </div>

        <div className="am-actions">
          {isSuperAdmin && (
            <button
              className="am-secondary"
              onClick={() => navigate('/admin/department')}
            >소속관리</button>
          )}
          <button
            className="am-primary"
            onClick={() => navigate('/admin/new')}
          >관리자 등록</button>
        </div>
      </div>
    </div>
  );
}
